import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { loadCatalogDetail } from '@/features/catalog/queries';
import { publicTrySchemaQueryOptions } from '@/features/execution/queries';
import { isPrerenderRequest } from '@/features/prerender/buildInput';
import { loadPageMeasuredFigures } from '@/features/usage/pageQueries';
import { loadEndpointSample } from '@/data/samples/endpointSample';
import { endpointTitle } from '@/lib/seo/apiTitle';
import { titleRate } from '@/lib/seo/answerParts';
import { cheapest } from '@/lib/catalog';
import { endpointSchemaGraph } from '@/lib/seo/endpointSchema';
import { localizedLinks, localizedUrl } from '@/i18n/localeHead';
import { m } from '@/paraglide/messages';
import { EndpointDetailPage } from '../../../../pages/EndpointDetailPage';

export const Route = createFileRoute('/_site/api/$apiId/$endpointId')({
  staticData: { footer: 'slim', catalogText: true },
  // Authored endpoints once carried their underscored object key as the route
  // id, so `/api/linkedin/search_posts` was the live URL while the hyphenated
  // form every other endpoint uses rendered "Endpoint not found". Send the old
  // shape to the canonical one instead of dropping those inbound links.
  beforeLoad: ({ params }) => {
    if (!params.endpointId.includes('_')) return;
    throw redirect({
      to: '/api/$apiId/$endpointId',
      params: { ...params, endpointId: params.endpointId.replaceAll('_', '-') },
      statusCode: 301,
    });
  },
  // Resolve the platform detail in the loader so TanStack Start renders the
  // endpoint header (H1) and providers server-side - crawlable, not a
  // client-only "Loading…" shell.
  //
  // The measured figures load here for the reason the platform route above
  // already loads them: left to a client `useQuery` they reach humans and
  // nobody else. On this page that cost two things - the header's requests
  // stat served a literal "-", and the FAQ's reliability answer never existed
  // in the HTML at all, because a summary of nothing writes no sentence.
  loader: async ({ context, params }) => {
    const { detail, icon } = await loadCatalogDetail(context.queryClient, params.apiId, 'platform');
    // Unknown platform or unknown endpoint is a 404. Resolving the endpoint
    // here (rather than only in `head` and the component) is what turns
    // /api/<known>/<unknown> from a soft 200 into a real one.
    if (!detail) throw notFound();
    const endpoint = detail.endpoints.find((e) => e.id === params.endpointId);
    if (!endpoint) throw notFound();
    await loadPageMeasuredFigures(context.queryClient, { sku: endpoint.sku });
    // The request and response schemas, so the console and the reference table
    // render their fields into the HTML rather than after a browser fetch. Only
    // during the prerender crawl, where `fetchPublicTrySchema` answers from the
    // build's OpenAPI projection: on a live request this would be one gateway
    // call per render, and the browser already makes it when it needs it.
    // `prefetchQuery` so a schema the gateway cannot serve leaves the page
    // rendering exactly what it renders today instead of failing the build.
    if (isPrerenderRequest()) {
      await context.queryClient.prefetchQuery(publicTrySchemaQueryOptions(endpoint.sku));
    }
    // The hand-written sample for an endpoint the authored copy does not cover:
    // the hero draws it, and the field cards, console and download state it.
    const sample = endpoint.sampleResponse ? null : await loadEndpointSample(endpoint.sku);
    return { detail, icon, sample };
  },
  head: ({ params, loaderData }) => {
    const path = `/api/${params.apiId}/${params.endpointId}`;
    // A page that does not exist has no language versions to announce.
    const missing = { links: [{ rel: 'canonical', href: localizedUrl(path) }] };
    const detail = loaderData?.detail;
    if (!detail) return missing;
    const endpoint = detail.endpoints.find((e) => e.id === params.endpointId);
    if (!endpoint) return missing;
    const price = endpoint.providers.length > 0 ? cheapest(endpoint).price : undefined;
    const title = endpointTitle(params.apiId, detail.name, endpoint.action, price);
    // The catalog blurb says what the call returns; the price follows it because
    // the price question is the intent these pages win, and the blurb alone never
    // carried a figure.
    const blurb = endpoint.description.trim().replace(/([^.!?。！？])$/, '$1.');
    const description = price
      ? m.seo_endpoint_description({ blurb, rate: titleRate(price) })
      : blurb;
    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: localizedUrl(path) },
        // The root sets the sitewide card text; without these a shared endpoint
        // link previewed with the sitewide blurb instead of this endpoint.
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        // Service + Offer + BreadcrumbList. These are the deepest and most
        // numerous pages on the site and carried no page-level node at all
        // before this - only the sitewide Organization + WebSite graph.
        { 'script:ld+json': endpointSchemaGraph(detail, endpoint, params.apiId) },
      ],
      links: localizedLinks(path),
    };
  },
  component: EndpointDetailRoute,
});

function EndpointDetailRoute() {
  const { apiId, endpointId } = Route.useParams();
  const { detail, icon, sample } = Route.useLoaderData();
  return <EndpointDetailPage
      apiId={apiId}
      endpointId={endpointId}
      detail={detail}
      icon={icon}
      sample={sample}
      presentation="site"
    />;
}
